import { useMutation, useSuspenseQuery } from '@apollo/client';

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { isLlmOutputDynamicOutput } from '@eluve/graphql-types';

import type { OutputTemplateOption } from '../PromptTemplateDefaultForm';
import {
  type PromptTemplateReference,
  getUserPersonalSettingsQuery,
  updateUserPersonalSettingsMutation,
} from '../promptTemplateDefaults.operations';

import { PanelHeader } from './PanelHeader';

const NONE_VALUE = '__none__';

interface UserPersonalSettingsCardProps {
  tenantId: string;
  userId: string;
  userName: string;
  promptTemplates: PromptTemplateReference[];
  outputTemplates: OutputTemplateOption[];
}

export const UserPersonalSettingsCard: React.FC<
  UserPersonalSettingsCardProps
> = ({ tenantId, userId, userName, promptTemplates, outputTemplates }) => {
  const toast = useToast();

  const { data } = useSuspenseQuery(getUserPersonalSettingsQuery, {
    variables: { tenantId, userId },
  });

  const settings = data?.tenantUserSettingsByPk;
  const currentPromptTemplateId = settings?.promptTemplateId ?? null;
  const currentOutputTemplateId = settings?.llmOutputTemplateId ?? null;

  const [updateSettings] = useMutation(updateUserPersonalSettingsMutation, {
    onError: () => toast.error('Failed to update user settings'),
  });

  // Filter to medical notes templates (SOAP_NOTE + DYNAMIC_OUTPUT)
  const medicalNotesTemplates = promptTemplates.filter(
    (t) => t.outputType === 'SOAP_NOTE' || t.outputType === 'DYNAMIC_OUTPUT',
  );

  const selectedTemplate = medicalNotesTemplates.find(
    (t) => t.id === currentPromptTemplateId,
  );

  const showOutputTemplates = isLlmOutputDynamicOutput(
    selectedTemplate?.outputType,
  );

  const handlePromptTemplateChange = (value: string) => {
    const templateId = value === NONE_VALUE ? null : value;
    const newTemplate = medicalNotesTemplates.find((t) => t.id === templateId);

    // Clear output template if switching away from DYNAMIC_OUTPUT
    const newOutputTemplateId = isLlmOutputDynamicOutput(
      newTemplate?.outputType,
    )
      ? currentOutputTemplateId
      : null;

    updateSettings({
      variables: {
        tenantId,
        userId,
        promptTemplateId: templateId,
        llmOutputTemplateId: newOutputTemplateId,
      },
      optimisticResponse: {
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings' as const,
          tenantId,
          userId,
          promptTemplateId: templateId,
          llmOutputTemplateId: newOutputTemplateId,
        },
      },
    });
  };

  const handleOutputTemplateChange = (value: string) => {
    const templateId = value === NONE_VALUE ? null : value;
    updateSettings({
      variables: {
        tenantId,
        userId,
        promptTemplateId: currentPromptTemplateId,
        llmOutputTemplateId: templateId,
      },
      optimisticResponse: {
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings' as const,
          tenantId,
          userId,
          promptTemplateId: currentPromptTemplateId,
          llmOutputTemplateId: templateId,
        },
      },
    });
  };

  const handleClear = () => {
    updateSettings({
      variables: {
        tenantId,
        userId,
        promptTemplateId: null,
        llmOutputTemplateId: null,
      },
      optimisticResponse: {
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings' as const,
          tenantId,
          userId,
          promptTemplateId: null,
          llmOutputTemplateId: null,
        },
      },
    });
  };

  const hasPersonalPreferences =
    currentPromptTemplateId !== null || currentOutputTemplateId !== null;

  return (
    <Stack align="stretch">
      <PanelHeader
        title={<Text weight="semibold">Personal Settings</Text>}
        action={
          hasPersonalPreferences ? (
            <Button variant="outline" size="sm" onClick={handleClear}>
              Clear
            </Button>
          ) : undefined
        }
      />

      <Stack gap={3} align="stretch" className="px-3 py-3">
        <Text size="xs" color="tertiary">
          {userName}&apos;s personal template preferences
        </Text>

        <Stack gap={1} align="stretch">
          <Text size="xs">Preferred Chart Style</Text>
          <Select
            value={currentPromptTemplateId ?? NONE_VALUE}
            onValueChange={handlePromptTemplateChange}
          >
            <SelectTrigger className="text-text-primary">
              <SelectValue placeholder="Using org default" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>
                <Text size="xs" color="tertiary">
                  Use org default
                </Text>
              </SelectItem>
              {medicalNotesTemplates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>

        {showOutputTemplates && outputTemplates.length > 0 && (
          <Stack gap={1} align="stretch">
            <Text size="xs">Output Template</Text>
            <Select
              value={currentOutputTemplateId ?? NONE_VALUE}
              onValueChange={handleOutputTemplateChange}
            >
              <SelectTrigger className="text-text-primary">
                <SelectValue placeholder="Using org default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>
                  <Text size="xs" color="tertiary">
                    Use org default
                  </Text>
                </SelectItem>
                {outputTemplates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Stack>
        )}

        {!hasPersonalPreferences && (
          <Stack gap={2} direction="horizontal">
            <Text inline size="xs" color="tertiary">
              No personal preferences set — using org defaults
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
